<template>
    <div class="documentation">
        <div class="search-container">
            <div class="search-input-container">
                <div class="search-input-wrapper">
                    <input
                        v-model="searchQuery"
                        type="text"
                        class="search-input"
                        placeholder="Введите поисковый запрос..."
                        @keyup.enter="performSearch"
                        ref="searchInput"
                    />
                    <button class="search-button" @click="performSearch">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                        </svg>
                    </button>
                </div>
            </div>

            <!-- Состояние загрузки -->
            <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <span>Идет поиск...</span>
            </div>

            <!-- Результаты поиска -->
            <div v-else-if="hasSearchResults" class="search-results">
                <div class="results-info">
                    <span class="results-count">Найдено: {{ searchResult.length }}</span>
                    <span v-if="searchQuery" class="search-query">по запросу: "{{ searchQuery }}"</span>
                </div>

                <div class="results-container">
                    <div class="results-list">
                        <div v-for="(result, index) in searchResult" :key="index" class="result-card">
                            <div class="result-header">
                                <h2 class="result-title">
                                    <a :href="result.url" class="result-link">
                                        <span v-html="highlightText(result.page_title, searchQuery)"></span>
                                    </a>
                                </h2>

                                <div class="result-meta">
                                <span class="result-date" v-if="result.created_at">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                    </svg>
                                    {{ formatDate(result.created_at) }}
                                </span>
                                </div>
                            </div>

                            <div class="result-content">
                                <p class="result-excerpt" v-html="highlightText(result.excerpt, searchQuery)"></p>
                            </div>

                            <div class="result-footer">
                                <a :href="result.url || '#'" class="read-more-link">
                                    Читать полностью →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Нет результатов -->
            <div v-else-if="searchPerformed && !hasSearchResults" class="no-results">
                <div class="no-results-content">
                    <div class="no-results-icon">🔍</div>
                    <h3>Ничего не найдено</h3>
                    <p>Попробуйте изменить поисковый запрос или использовать другие ключевые слова</p>
                </div>
            </div>

            <!-- Начальное состояние -->
            <div v-else class="initial-state">
                <div class="initial-content">
                    <div class="initial-icon">📚</div>
                    <h2>Добро пожаловать в поиск</h2>
                    <p>Введите запрос в поле выше, чтобы найти нужные документы</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'SearchDocumentationComponent',
    props: {
        client: {
            type: String,
            default: ''
        },
        search_query: {
            type: String,
            default: ''
        },
        search_result: {
            type: [Array, Object],
            default: () => []
        },
        search_route: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            searchQuery: this.search_query,
            searchResult: [],
            loading: false,
            searchPerformed: false
        };
    },

    computed: {
        hasSearchResults() {
            return Array.isArray(this.searchResult) && this.searchResult.length > 0;
        }
    },

    methods: {
        async performSearch() {
            if (!this.searchQuery.trim() || this.searchQuery.length < 3) return;

            this.loading = true;
            this.searchPerformed = true;

            try {
                await this.search();
            } catch (error) {
                console.error('Ошибка поиска:', error);
                this.searchResult = [];
            } finally {
                this.loading = false;
            }
        },

        async search() {
            try {
                const response = await axios.post(this.search_route, {
                    query: this.searchQuery,
                    client: this.client
                });

                console.log('Ответ от сервера:', response.data);

                // Обрабатываем ответ
                this.searchResult = this.extractResults(response.data);

                console.log('Извлеченные результаты:', this.searchResult);

            } catch (error) {
                console.error('Ошибка при выполнении запроса:', error);
                if (error.response) {
                    console.error('Данные ошибки:', error.response.data);
                    this.searchResult = this.extractResults(error.response.data);
                } else {
                    this.searchResult = [];
                }
            }
        },

        extractResults(responseData) {
            console.log('Извлечение результатов из:', responseData);

            // 1. Если это уже массив - возвращаем как есть
            if (Array.isArray(responseData)) {
                console.log('Найден массив в корне ответа');
                return responseData;
            }

            // 2. Если это объект
            if (responseData && typeof responseData === 'object') {
                // 2.1. Проверяем поле foss (как на скриншоте)
                if (responseData.foss && typeof responseData.foss === 'object') {
                    console.log('Найдено поле foss');

                    // Проверяем, является ли foss массивом
                    if (Array.isArray(responseData.foss)) {
                        console.log('foss является массивом');
                        return responseData.foss;
                    }

                    // Если foss - объект с числовыми ключами, преобразуем в массив
                    const fossKeys = Object.keys(responseData.foss);
                    if (fossKeys.length > 0 && fossKeys.every(key => !isNaN(key))) {
                        console.log('foss содержит числовые ключи - преобразуем в массив');
                        return Object.values(responseData.foss);
                    }

                    // Если foss - обычный объект, пробуем найти в нем массивы
                    for (const key in responseData.foss) {
                        if (Array.isArray(responseData.foss[key])) {
                            console.log(`Найден массив в foss.${key}`);
                            return responseData.foss[key];
                        }
                    }
                }

                // 2.2. Проверяем другие возможные поля
                const arrayFields = ['items', 'data', 'results', 'docs', 'documents', 'searchResults'];
                for (const field of arrayFields) {
                    if (responseData[field] && Array.isArray(responseData[field])) {
                        console.log(`Найден массив в поле ${field}`);
                        return responseData[field];
                    }
                }

                // 2.3. Если весь объект имеет числовые ключи, преобразуем в массив
                const allKeys = Object.keys(responseData);
                if (allKeys.length > 0 && allKeys.every(key => !isNaN(key))) {
                    console.log('Объект с числовыми ключами - преобразуем в массив');
                    return Object.values(responseData);
                }

                // 2.4. Ищем любой вложенный объект с числовыми ключами
                for (const key in responseData) {
                    if (responseData[key] && typeof responseData[key] === 'object') {
                        const nestedKeys = Object.keys(responseData[key]);
                        if (nestedKeys.length > 0 && nestedKeys.every(nestedKey => !isNaN(nestedKey))) {
                            console.log(`Найден объект с числовыми ключами в поле ${key}`);
                            return Object.values(responseData[key]);
                        }
                    }
                }

                console.warn('Не удалось найти массив в ответе:', responseData);
            }

            return [];
        },

        highlightText(text, query) {
            if (!query || !text) return text || '';
            try {
                const cleanQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(`(${cleanQuery})`, 'gi');
                return text.toString().replace(regex, '<mark class="search-highlight">$1</mark>');
            } catch (e) {
                return text;
            }
        },

        formatDate(dateString) {
            if (!dateString) return '';
            try {
                const date = new Date(dateString);
                return isNaN(date.getTime()) ? dateString : date.toLocaleDateString('ru-RU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            } catch (e) {
                return dateString;
            }
        }
    },

    mounted() {
        console.log('Инициализация компонента');

        // Обрабатываем начальные данные
        if (this.search_result) {
            this.searchResult = this.extractResults(this.search_result);
        }

        // Если при загрузке уже есть поисковый запрос, выполняем поиск
        if (this.searchQuery && this.searchQuery.trim()) {
            this.performSearch();
        }
    }
};
</script>

<style scoped>
.search-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem 1rem;
}

.search-input-container {
    margin-bottom: 2rem;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-input {
    width: 100%;
    padding: 1rem 1.5rem;
    padding-right: 3.5rem;
    border: 2px solid #e2e8f0;
    border-radius: 50px;
    font-size: 1rem;
    outline: none;
    transition: border-color 0.3s ease;
}

.search-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-button {
    position: absolute;
    right: 0.5rem;
    background: #3b82f6;
    border: none;
    border-radius: 50%;
    width: 46px;
    height: 46px;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.search-button:hover {
    background: #2563eb;
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: #64748b;
}

.loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid #e5e7eb;
    border-top: 2px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-right: 0.75rem;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.results-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.results-count {
    font-weight: 600;
    color: #1f2937;
}

.search-query {
    color: #6b7280;
    font-size: 0.9rem;
}

.results-list {
    display: flex;
    flex-direction: column;
}

.result-card {
    background: white;
    padding: 1.5rem;
    border-bottom: 1px solid #e2e8f0;
    transition: all 0.3s ease;
}

.result-card:last-child {
    border-bottom: none;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
}

.result-card:first-child {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
}

.result-card:hover {
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.result-header {
    margin-bottom: 1rem;
}

.result-title {
    margin: 0 0 0.5rem 0;
    font-size: 1.25rem;
    line-height: 1.4;
}

.result-link {
    color: #1f2937;
    text-decoration: none;
    transition: color 0.3s ease;
}

.result-link:hover {
    color: #3b82f6;
}

.result-meta {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 0.875rem;
    color: #6b7280;
}

.result-meta span {
    display: flex;
    align-items: center;
    gap: 0.375rem;
}

.result-content {
    margin-bottom: 1rem;
}

.result-excerpt {
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
}

.result-footer {
    display: flex;
    justify-content: flex-end;
}

.read-more-link {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.3s ease;
}

.read-more-link:hover {
    color: #2563eb;
}

.no-results,
.initial-state {
    text-align: center;
    padding: 3rem 1rem;
}

.no-results-content,
.initial-content {
    max-width: 400px;
    margin: 0 auto;
}

.no-results-icon,
.initial-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
}

.no-results h3,
.initial-state h2 {
    color: #1f2937;
    margin-bottom: 0.5rem;
    font-size: 1.5rem;
}

.no-results p,
.initial-state p {
    color: #6b7280;
    line-height: 1.6;
}

/* Стили для подсветки */
:deep(.search-highlight) {
    background-color: #fef3c7;
    padding: 0.125rem 0;
    border-radius: 0.25rem;
    font-weight: 600;
}

/* Адаптивность */
@media (max-width: 640px) {
    .search-container {
        padding: 1rem;
    }

    .search-input {
        padding: 0.875rem 1.25rem;
        padding-right: 3rem;
    }

    .search-button {
        width: 40px;
        height: 40px;
        right: 0.375rem;
    }

    .results-container {
        max-height: 400px;
    }

    .result-card {
        padding: 1.25rem;
    }

    .result-title {
        font-size: 1.125rem;
    }
}

@media (max-width: 480px) {
    .results-container {
        max-height: 350px;
    }
}
</style>
